
'use client';

import { useState, useEffect } from 'react';
import type { Post, ReactionType } from '@/types';
import Link from 'next/link';
import { formatDistanceToNow } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { MessageSquare, MoreHorizontal, Share2, ThumbsUp, Heart, HandHelping, Pencil, Trash2, Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { useAuth } from '@/context/AuthContext';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { ImageLightbox } from './image-lightbox';
import { usePosts } from '@/context/PostContext';
import { usePortfolio } from '@/context/PortfolioContext';
import { Textarea } from './ui/textarea';

interface PostCardProps {
  post: Post;
}

const reactionIcons = {
    like: ThumbsUp,
    heart: Heart,
    support: HandHelping
};

const reactionColors = {
    like: 'text-blue-500 fill-blue-500',
    heart: 'text-red-500 fill-red-500',
    support: 'text-purple-500 fill-purple-500'
}

const ImageGrid = ({ images, onImageClick }: { images: string[], onImageClick: (index: number) => void }) => {
    const count = images.length;

    if (count === 0) return null;

    if (count === 1) {
        return (
            <div className="cursor-pointer" onClick={() => onImageClick(0)}>
                <Image src={images[0]} alt="Post image 1" width={800} height={600} className="w-full h-auto object-cover" />
            </div>
        );
    }
    
    if (count === 2) {
        return (
            <div className="grid grid-cols-2 gap-1">
                {images.map((src, i) => (
                    <div key={i} className="cursor-pointer" onClick={() => onImageClick(i)}>
                        <Image src={src} alt={`Post image ${i + 1}`} width={400} height={400} className="w-full h-full object-cover" />
                    </div>
                ))}
            </div>
        )
    }

    if (count === 3) {
        return (
            <div className="grid grid-cols-2 grid-rows-2 gap-1 h-[400px]">
                <div className="col-span-1 row-span-2 cursor-pointer" onClick={() => onImageClick(0)}>
                     <Image src={images[0]} alt={`Post image 1`} width={400} height={400} className="w-full h-full object-cover" />
                </div>
                 <div className="col-span-1 row-span-1 cursor-pointer" onClick={() => onImageClick(1)}>
                     <Image src={images[1]} alt={`Post image 2`} width={400} height={200} className="w-full h-full object-cover" />
                </div>
                 <div className="col-span-1 row-span-1 cursor-pointer" onClick={() => onImageClick(2)}>
                     <Image src={images[2]} alt={`Post image 3`} width={400} height={200} className="w-full h-full object-cover" />
                </div>
            </div>
        )
    }

    // 4 or more images
    const remainingCount = count - 4;
    return (
        <div className="grid grid-cols-2 grid-rows-2 gap-1 h-[400px]">
            {images.slice(0, 4).map((src, i) => (
                <div key={i} className="relative cursor-pointer" onClick={() => onImageClick(i)}>
                    <Image src={src} alt={`Post image ${i + 1}`} width={400} height={400} className="w-full h-full object-cover" />
                    {i === 3 && remainingCount > 0 && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <span className="text-white text-3xl font-bold">+{remainingCount}</span>
                        </div>
                    )}
                </div>
            ))}
        </div>
    )
};


export function PostCard({ post }: PostCardProps) {
  // Determine if this is a portfolio post by checking if the author handle is not the owner's UID
  const { user, isOwner } = useAuth();
  const isPortfolioPost = user?.uid !== post.author.handle;

  const { deletePost, updatePost, addComment, handleReaction } = isPortfolioPost ? usePortfolio() : usePosts();
  
  const router = useRouter();

  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(post.content);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [timeAgo, setTimeAgo] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  useEffect(() => {
    const updateTimestamp = () => {
      if (post.timestamp) {
        setTimeAgo(formatDistanceToNow(post.timestamp, { addSuffix: true, locale: enUS }));
      }
    };
    updateTimestamp();
    const interval = setInterval(updateTimestamp, 60000);
    return () => clearInterval(interval);
  }, [post.timestamp]);

  const handleUpdate = () => {
    const isContentEmpty = !editedContent.trim();
    if (!isContentEmpty && editedContent !== post.content) {
      updatePost(post.id, editedContent);
    }
    setIsEditing(false);
  };

  const handleCommentSubmit = () => {
    if (!user) {
      router.push('/login');
      return;
    }
    if (newComment.trim()) {
        addComment(post.id, newComment.trim());
        setNewComment('');
    }
  };

  const handleReactionClick = (reaction: ReactionType) => {
    if (!user) {
      router.push('/login');
      return;
    }
    handleReaction(post.id, reaction);
  }

  const currentReaction = post.reaction;
  const LikeButtonIcon = currentReaction ? reactionIcons[currentReaction] : ThumbsUp;
  const likeButtonText = currentReaction ? 
    currentReaction.charAt(0).toUpperCase() + currentReaction.slice(1) 
    : "Like";

  const isPostOwner = isOwner;
  const hasMedia = (post.imageUrls && post.imageUrls.length > 0) || post.sketchfabEmbedUrl;
  
  const authorProfileLink = `/friends/${post.author.handle}`;
  const hasContent = post.content && post.content.trim() !== '';
  
  const vipClass = post.isVIP ? {
    'style1': 'vfx-japanese-frame',
    'style2': 'chinese-frame',
    'style3': 'vfx-bubble-ripple-frame',
  }[post.vipStyle || 'style1'] : '';

  const vipContentClass = post.isVIP ? {
    'style1': 'vfx-japanese-frame-content',
    'style2': 'chinese-frame-content',
    'style3': 'vfx-bubble-ripple-frame-content',
  }[post.vipStyle || 'style1'] : '';


  return (
    <>
    <Card className={cn("overflow-visible", vipClass)}>
       <div className={cn("h-full", vipContentClass)}>
        {post.vipStyle === 'style2' && (
            <div className="particles">
                {Array.from({ length: 9 }).map((_, i) => <span key={i} />)}
            </div>
        )}
        {post.vipStyle === 'style3' && (
           <>
            <div className="bubble-overlay"></div>
            <div className="center-ripple"></div>
           </>
        )}
        <CardContent className="p-0">
          <div className="p-4 pb-2">
              <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                  <Avatar>
                  <AvatarImage src={post.author.avatarUrl} alt={post.author.name} data-ai-hint="person portrait" />
                  <AvatarFallback>{post.author.name.split(' ').map(n=>n[0]).join('')}</AvatarFallback>
                  </Avatar>
                  <div>
                  <Link href={authorProfileLink}>
                      <p className="font-semibold hover:underline">{post.author.name}</p>
                  </Link>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{timeAgo}</span>
                      {post.category && (
                          <>
                          <span>·</span>
                          <Link href={`/category/${post.category.toLowerCase().replace(/ /g, '-')}`} className="hover:underline">
                              <span>{post.category}</span>
                          </Link>
                          </>
                      )}
                  </div>
                  </div>
              </div>
              {isPostOwner && (
                  <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-5 w-5" />
                      </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setIsEditing(true)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      <span>Edit</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => deletePost(post.id)} className="text-red-500">
                      <Trash2 className="mr-2 h-4 w-4" />
                      <span>Delete</span>
                      </DropdownMenuItem>
                  </DropdownMenuContent>
                  </DropdownMenu>
              )}
              </div>

              {isEditing ? (
              <div className="my-4 px-4">
                  <Textarea 
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="w-full"
                  />
                  <div className="flex justify-end gap-2 mt-2">
                  <Button variant="ghost" size="sm" onClick={() => {
                      setIsEditing(false);
                      setEditedContent(post.content);
                  }}>Cancel</Button>
                  <Button size="sm" onClick={handleUpdate}>Save</Button>
                  </div>
              </div>
              ) : (
              hasContent && <p className={cn("my-4 text-sm whitespace-pre-line px-4", hasMedia && "mb-0")}>{post.content}</p>
              )}
          </div>
          
          {post.sketchfabEmbedUrl ? (
            <div className={cn("aspect-video bg-card-foreground", hasContent && "mt-4")}>
              <iframe
                title="Embedded Content"
                src={post.sketchfabEmbedUrl}
                frameBorder="0"
                allow="autoplay; fullscreen; vr; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>
          ) : post.imageUrls && post.imageUrls.length > 0 && (
              <div className={cn("bg-card-foreground", hasContent && "mt-4")}>
                  <ImageGrid images={post.imageUrls} onImageClick={openLightbox} />
              </div>
          )}

          <div className="p-4 pt-2">
              <div className="flex justify-between items-center text-sm text-muted-foreground mb-2">
                  <div>{post.likes > 0 && `${post.likes} reactions`}</div>
                  <div>{post.comments.length > 0 && `${post.comments.length} comments`}</div>
              </div>

              <Separator />

              <div className="grid grid-cols-3 gap-2 mt-2">
              <div className="relative group">
                  <div className="reaction-popover">
                      <ThumbsUp onClick={() => handleReactionClick('like')} className="reaction-icon reaction-like" />
                      <Heart onClick={() => handleReactionClick('heart')} className="reaction-icon reaction-heart" />
                      <HandHelping onClick={() => handleReactionClick('support')} className="reaction-icon reaction-support" />
                  </div>
                  <Button
                  variant="ghost"
                  className="w-full flex items-center justify-center gap-2"
                  onClick={() => handleReactionClick(currentReaction === 'like' ? null : 'like')}
                  disabled={!user}
                  >
                  <LikeButtonIcon
                      className={cn('h-5 w-5', currentReaction && reactionColors[currentReaction])}
                  />
                  <span
                      className={cn(
                      'font-semibold text-muted-foreground',
                      currentReaction && reactionColors[currentReaction].split(' ')[0]
                      )}
                  >
                      {likeButtonText}
                  </span>
                  </Button>
              </div>
              <Button variant="ghost" className="flex items-center justify-center gap-2" onClick={() => setShowComments(!showComments)}>
                  <MessageSquare className="h-5 w-5" />
                  <span className="font-semibold text-muted-foreground">Comment</span>
              </Button>
              <Button variant="ghost" className="flex items-center justify-center gap-2">
                  <Share2 className="h-5 w-5" />
                  <span className="font-semibold text-muted-foreground">Share</span>
              </Button>
              </div>

              {showComments && (
                  <div className="mt-4 pt-4 border-t">
                  {user ? (
                      <div className="flex items-start gap-3">
                          <Avatar className="h-8 w-8">
                              <AvatarImage src={user?.photoURL || "https://placehold.co/40x40.png"} alt={user?.displayName || "User"} data-ai-hint="person portrait" />
                              <AvatarFallback>{user?.displayName?.split(' ').map(n=>n[0]).join('') || 'U'}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 flex items-center gap-2 bg-secondary rounded-full px-3">
                              <Textarea
                                  value={newComment}
                                  onChange={(e) => setNewComment(e.target.value)}
                                  placeholder="Write a comment..."
                                  className="flex-1 bg-transparent border-none focus-visible:ring-0 resize-none h-auto py-2"
                                  rows={1}
                              />
                              <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={handleCommentSubmit} disabled={!newComment.trim()}>
                                  <Send className="h-4 w-4" />
                              </Button>
                          </div>
                      </div>
                  ) : (
                      <div className="text-center text-sm text-muted-foreground">
                      <Link href="/login" className="text-primary hover:underline">Log in</Link> to comment.
                      </div>
                  )}
                      <div className="mt-4 space-y-4">
                          {post.comments.map((comment, index) => (
                              <div key={index} className="flex items-start gap-3">
                                  <Avatar className="h-8 w-8">
                                      <AvatarImage src={comment.author.avatarUrl} alt={comment.author.name} data-ai-hint="person portrait" />
                                      <AvatarFallback>{comment.author.name.split(' ').map(n=>n[0]).join('')}</AvatarFallback>
                                  </Avatar>
                                  <div className="flex-1">
                                      <div className="bg-secondary rounded-xl p-3">
                                          <p className="font-semibold text-sm">{comment.author.name}</p>
                                          <p className="text-sm">{comment.content}</p>
                                      </div>
                                      <span className="text-xs text-muted-foreground px-3">{comment.timestamp ? formatDistanceToNow(new Date(comment.timestamp as any), { addSuffix: true, locale: enUS }) : 'just now'}</span>
                                  </div>
                              </div>
                          ))}
                      </div>
                  </div>
              )}
          </div>
        </CardContent>
      </div>
    </Card>
    {lightboxOpen && post.imageUrls && (
        <ImageLightbox
            images={post.imageUrls}
            startIndex={lightboxIndex}
            onClose={() => setLightboxOpen(false)}
        />
    )}
    </>
  );
}
